import Dashboard from '@shared/components/Dashboard/Dashboard';
import type { DashboardProps } from '@shared/components/Dashboard/DashboardProps';
import CustomerCreditDashboard from '@components/CustomerCreditDashboard/CustomerCreditDashboard';
import type { CustomerCreditDashboardResponse } from '@api/customerCreditDashboard';
import { useState } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './CustomerDashboard.module.scss';

const dashboardTabs = ['qa', 'users', 'credits'] as const;
type DashboardTab = typeof dashboardTabs[number];

type CustomerDashboardProp =
  | 'setFilters'
  | 'filterDate'
  | 'theme'
  | 'organisationId'
  | 'userRequest'
  | 'askedQuestionsRequest'
  | 'averageQuestionsRequest'
  | 'organisationUsersRequest'
  | 'totalTransactionsRequest'
  | 'insightsUsageRequest'
  | 'behaviouralUsageRequest'
  | 'feedbackRatingsRequest'
  | 'userBehavioursRequest'
  | 'averageDocumentsRequest'
  | 'callbackRequestsRequest'
  | 'showExtendedCallbackStatuses'
  | 'exportQA'
  | 'onExportFailure'
  | 'queryArgs';

export type CustomerDashboardProps = Pick<DashboardProps, CustomerDashboardProp> & {
  creditDashboardRequest: {
    data?: CustomerCreditDashboardResponse;
    isError: boolean;
    isLoading: boolean;
  };
};

const CustomerDashboard = ({ creditDashboardRequest, ...dashboardProps }: CustomerDashboardProps) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('credits');
  const { t } = useTranslation();

  return (
    <Dashboard
      {...dashboardProps}
      dashboardView={activeTab === 'credits' ? 'controls' : activeTab}
      renderContent={(content) => (
        <Tabs
          activeKey={activeTab}
          aria-label={t('dashboardTabs.ariaLabel')}
          className={styles.tabs}
          destroyOnHidden
          onChange={(key) => setActiveTab(key as DashboardTab)}
          items={dashboardTabs.map((tab) => ({
            key: tab,
            label: t(`dashboardTabs.${tab}`),
            children: activeTab !== tab ? null : tab === 'credits'
              ? <CustomerCreditDashboard {...creditDashboardRequest} theme={dashboardProps.theme} />
              : <div className={styles.content} data-testid="customer-dashboard-sections">{content}</div>,
          }))}
        />
      )}
    />
  );
};

export default CustomerDashboard;
